/**
 * Small pill-shaped label for the learner experience.
 */
import type { ReactNode, CSSProperties } from 'react';

export type BadgeVariant =
  | 'default'
  | 'surface'
  | 'muted'
  | 'accent'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  style?: CSSProperties;
  className?: string;
}

const VARIANT_STYLES: Record<BadgeVariant, CSSProperties> = {
  default: { color: 'var(--color-text-muted)', background: 'var(--color-surface-muted)' },
  surface: {
    color: 'var(--color-text-secondary)',
    background: 'var(--color-surface-solid)',
    border: '1px solid var(--color-surface-border)',
  },
  muted: {
    color: 'var(--color-text-secondary)',
    background: 'var(--color-surface-muted)',
  },
  accent: { color: 'var(--color-accent-text)', background: 'var(--color-accent-subtle)' },
  info: { color: 'var(--color-info-text)', background: 'var(--color-info-subtle)' },
  success: { color: 'var(--color-success-text)', background: 'var(--color-success-subtle)' },
  warning: { color: 'var(--color-warning-text)', background: 'var(--color-warning-subtle)' },
  error: { color: 'var(--color-error-text)', background: 'var(--color-error-subtle)' },
};

export function Badge({ children, variant = 'default', style, className }: BadgeProps) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--weight-medium)',
        fontFamily: 'var(--font-sans)',
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-full)',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...VARIANT_STYLES[variant],
        ...style,
      }}
    >
      {children}
    </span>
  );
}
